<template>
  <div
    class="circular-slider"
    :class="{ 'circular-slider--disabled': disabled }"
    :style="{ width: size + 'px', height: size + 'px' }"
  >
    <svg
      ref="svgRef"
      :viewBox="`0 0 ${size} ${size}`"
      :width="size"
      :height="size"
    >
      <!-- Piste complète (fond) -->
      <path
        :d="trackPath"
        fill="none"
        :stroke="trackColor"
        :stroke-width="strokeWidth"
        stroke-linecap="round"
      />

      <!-- Zone de clic élargie et invisible, pour pouvoir cliquer/glisser
           n'importe où sur la piste, pas seulement sur le curseur -->
      <path
        :d="trackPath"
        fill="none"
        stroke="transparent"
        :stroke-width="strokeWidth + 24"
        stroke-linecap="round"
        style="cursor: pointer"
        @pointerdown="onPointerDown"
      />

      <!-- Arc de progression -->
      <path
        :d="fillPath"
        fill="none"
        :stroke="color"
        :stroke-width="strokeWidth"
        stroke-linecap="round"
      />

      <!-- Curseur (poignée) -->
      <circle
        :cx="handle.x"
        :cy="handle.y"
        :r="strokeWidth * 0.72"
        :fill="handleColor"
        :stroke="handleBorderColor"
        stroke-width="2"
        style="cursor: grab"
        tabindex="0"
        role="slider"
        :aria-valuemin="min"
        :aria-valuemax="max"
        :aria-valuenow="modelValue"
        @pointerdown="onPointerDown"
        @keydown="onKeydown"
      />
    </svg>

    <!-- Contenu central (valeur affichée) -->
    <div class="circular-slider__label" :style="{ color }">
      <slot :value="modelValue">
        <span class="circular-slider__value">{{ displayValue }}</span>
        <span v-if="unit" class="circular-slider__unit">{{ unit }}</span>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, defineProps, defineEmits } from 'vue'

const props = defineProps({
  modelValue: { type: Number, required: true },
  min: { type: Number, default: 0 },
  max: { type: Number, default: 100 },
  step: { type: Number, default: 1 },
  size: { type: Number, default: 260 },
  strokeWidth: { type: Number, default: 26 },
  // Angle (en degrés) de l'ouverture en bas du cercle. 0 = cercle complet.
  gapAngle: { type: Number, default: 70 },
  color: { type: String, default: '#5CE6A6' },
  trackColor: { type: String, default: 'rgba(92, 230, 166, 0.25)' },
  handleColor: { type: String, default: '#ffffff' },
  handleBorderColor: { type: String, default: '#c9c9c9' },
  unit: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  // Si true, la piste représente toujours l'échelle 0-100 : min/max ne font
  // que restreindre la portion atteignable, sans l'étirer. Si false, min/max
  // définissent eux-mêmes les bornes de l'échelle (comportement classique).
  keepBaseScale: { type: Boolean, default: true },
})

const emit = defineEmits(['update:modelValue', 'change'])

const svgRef = ref(null)
let dragging = false

/* ---------- Géométrie ---------- */

const cx = computed(() => props.size / 2)
const cy = computed(() => props.size / 2)
const radius = computed(() => (props.size - props.strokeWidth) / 2 - 2)

// Angles exprimés en degrés, 0° = midi, sens horaire.
// L'ouverture (gap) est centrée en bas du cercle (180°).
const trackStart = computed(() => 180 + props.gapAngle / 2)
const sweep = computed(() => 360 - props.gapAngle)
const trackEnd = computed(() => trackStart.value + sweep.value)

function clamp(v, lo, hi) {
  return Math.min(Math.max(v, lo), hi)
}

function polarToCartesian(angleDeg, r = radius.value) {
  const rad = (angleDeg * Math.PI) / 180
  return {
    x: cx.value + r * Math.sin(rad),
    y: cy.value - r * Math.cos(rad),
  }
}

function describeArc(startAngle, endAngle, r = radius.value) {
  const start = polarToCartesian(startAngle, r)
  const end = polarToCartesian(endAngle, r)
  const largeArc = endAngle - startAngle > 180 ? 1 : 0
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`
}

const trackPath = computed(() => describeArc(trackStart.value, trackEnd.value))

const scaleMin = computed(() => (props.keepBaseScale ? 0 : props.min))
const scaleMax = computed(() => (props.keepBaseScale ? 100 : props.max))

const valueAngle = computed(() => {
  const ratio = (clamp(props.modelValue, props.min, props.max) - scaleMin.value) / (scaleMax.value - scaleMin.value)
  return trackStart.value + ratio * sweep.value
})

const fillPath = computed(() => describeArc(trackStart.value, valueAngle.value))
const handle = computed(() => polarToCartesian(valueAngle.value))

const displayValue = computed(() => Math.round(props.modelValue))

/* ---------- Conversion angle <-> valeur ---------- */

function angleToValue(angle) {
  const ratio = (angle - trackStart.value) / sweep.value
  let v = scaleMin.value + ratio * (scaleMax.value - scaleMin.value)
  v = Math.round(v / props.step) * props.step
  return clamp(v, props.min, props.max)
}

// Ramène un angle brut (0-360°) sur la portion valide de la piste,
// en gérant le passage par le "gap" du bas.
function clampAngleToTrack(theta) {
  const diff = ((theta - trackStart.value) % 360 + 360) % 360
  if (diff <= sweep.value) return trackStart.value + diff
  const distToEnd = diff - sweep.value
  const distToStart = 360 - diff
  return distToEnd < distToStart ? trackEnd.value : trackStart.value
}

function angleFromPointerEvent(e) {
  const rect = svgRef.value.getBoundingClientRect()
  const centerX = rect.left + rect.width / 2
  const centerY = rect.top + rect.height / 2
  const dx = e.clientX - centerX
  const dy = e.clientY - centerY
  let theta = (Math.atan2(dx, -dy) * 180) / Math.PI
  if (theta < 0) theta += 360
  return theta
}

/* ---------- Interaction pointeur ---------- */

function updateFromPointer(e) {
  const theta = angleFromPointerEvent(e)
  const angle = clampAngleToTrack(theta)
  const value = angleToValue(angle)
  if (value !== props.modelValue) {
    emit('update:modelValue', value)
  }
}

function onPointerDown(e) {
  if (props.disabled) return
  dragging = true
  e.target.setPointerCapture?.(e.pointerId)
  e.target.addEventListener('pointermove', onPointerMove)
  e.target.addEventListener('pointerup', onPointerUp, { once: true })
  e.target.addEventListener('pointercancel', onPointerUp, { once: true })
  updateFromPointer(e)
}

function onPointerMove(e) {
  if (!dragging) return
  updateFromPointer(e)
}

function onPointerUp(e) {
  dragging = false
  e.target.removeEventListener('pointermove', onPointerMove)
  emit('change', props.modelValue)
}

/* ---------- Accessibilité clavier ---------- */

function onKeydown(e) {
  if (props.disabled) return
  let delta = 0
  if (e.key === 'ArrowUp' || e.key === 'ArrowRight') delta = props.step
  else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') delta = -props.step
  else return
  e.preventDefault()
  const value = clamp(props.modelValue + delta, props.min, props.max)
  emit('update:modelValue', value)
  emit('change', value)
}
</script>

<style scoped>
.circular-slider {
  position: relative;
  display: inline-block;
  user-select: none;
  touch-action: none;
}

.circular-slider--disabled {
  opacity: 0.5;
  pointer-events: none;
}

.circular-slider svg {
  display: block;
}

.circular-slider__label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-family: system-ui, -apple-system, sans-serif;
  pointer-events: none;
}

.circular-slider__value {
  font-size: 3rem;
  font-weight: 600;
  line-height: 1;
}

.circular-slider__unit {
  font-size: 1.25rem;
  font-weight: 500;
  opacity: 0.9;
}
</style>
